import * as React from 'react';
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
  DrawerClose,
} from './drawer';
import { Button } from './button';

export interface ActionDrawerProps {
  /** Controls whether the drawer is open */
  open: boolean;
  /** Callback when the drawer open state changes */
  onOpenChange: (open: boolean) => void;
  /** Title displayed in the drawer header */
  title: string;
  /** Description displayed below the title */
  description: string;
  /** Label for the confirm button */
  confirmLabel: string;
  /** Label for the cancel button */
  cancelLabel: string;
  /** Variant for the confirm button */
  confirmVariant?: 'default' | 'destructive';
  /** Callback when confirm button is clicked */
  onConfirm: () => void;
  /** Shows loading state on confirm button */
  isLoading?: boolean;
  /** Loading text shown on confirm button when isLoading is true */
  loadingLabel?: string;
  /** Optional content rendered between header and footer */
  children?: React.ReactNode;
}

/**
 * A standardized action drawer component that provides a consistent layout
 * for confirmation dialogs and forms.
 *
 * Layout:
 * - Header with title and description
 * - Optional children slot for custom content
 * - Footer with confirm and cancel buttons
 *
 * @example
 * // Simple confirmation
 * <ActionDrawer
 *   open={isOpen}
 *   onOpenChange={setIsOpen}
 *   title="Delete Item?"
 *   description="This action cannot be undone."
 *   confirmLabel="Delete"
 *   cancelLabel="Cancel"
 *   confirmVariant="destructive"
 *   onConfirm={handleDelete}
 * />
 *
 * @example
 * // With custom content
 * <ActionDrawer
 *   open={isOpen}
 *   onOpenChange={setIsOpen}
 *   title="Publish Event?"
 *   description="Select notification options:"
 *   confirmLabel="Publish"
 *   cancelLabel="Cancel"
 *   onConfirm={handlePublish}
 *   isLoading={isPublishing}
 * >
 *   <Checkbox ... />
 *   <Textarea ... />
 * </ActionDrawer>
 */
export function ActionDrawer({
  open,
  onOpenChange,
  title,
  description,
  confirmLabel,
  cancelLabel,
  confirmVariant = 'default',
  onConfirm,
  isLoading = false,
  loadingLabel = 'Loading...',
  children,
}: ActionDrawerProps) {
  return (
    <Drawer open={open} onOpenChange={onOpenChange} direction="bottom">
      <DrawerContent>
        <div className="mx-auto w-full max-w-sm">
          <DrawerHeader>
            <DrawerTitle>{title}</DrawerTitle>
            <DrawerDescription>{description}</DrawerDescription>
          </DrawerHeader>
          {children && <div className="py-4 space-y-4">{children}</div>}
          <DrawerFooter>
            <Button
              onClick={onConfirm}
              disabled={isLoading}
              variant={confirmVariant}
              className="h-16"
            >
              {isLoading ? loadingLabel : confirmLabel}
            </Button>
            <DrawerClose asChild>
              <Button variant="outline" className="h-16">
                {cancelLabel}
              </Button>
            </DrawerClose>
          </DrawerFooter>
        </div>
      </DrawerContent>
    </Drawer>
  );
}

